// BuildOrderConfigurator.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    ChevronDown, ChevronUp, Upload, Plus, Trash2, ArrowLeft, ArrowRight,
    FileJson, Check, Eye, X, Save, FolderOpen, AlertTriangle
} from "lucide-react";
import { addCiv, fetchCivs, updateCiv, removeCiv } from "../services/frontendAPI";

/* ---------- tiny utils ---------- */
const fmt = (s) => {
    const m = Math.floor(s / 60).toString().padStart(2, "0");
    const ss = Math.floor(s % 60).toString().padStart(2, "0");
    return `${m}:${ss}`;
};
const parseTime = (timeStr) => {
    if (!timeStr) return 0;
    const parts = timeStr.split(":");
    if (parts.length === 1) return parseInt(parts[0]) || 0;
    if (parts.length === 2) {
        const minutes = parseInt(parts[0]) || 0;
        const seconds = parseInt(parts[1]) || 0;
        return minutes * 60 + seconds;
    }
    return 0;
};
const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));
const newUid = () => `${Date.now().toString(36)}_${Math.random().toString(36).slice(2)}`;

/* ---------- Aurora Dark theme tokens ---------- */
const AURORA_BG = {
    backgroundColor: "#0b1220",
    backgroundImage: `
    radial-gradient(1200px 800px at -20% -10%, rgba(14,165,233,.25), transparent 60%),
    radial-gradient(1200px 800px at 120% 0%, rgba(34,211,238,.22), transparent 55%),
    radial-gradient(1000px 800px at 110% 120%, rgba(168,85,247,.18), transparent 55%),
    radial-gradient(800px 600px at 0% 120%, rgba(16,185,129,.16), transparent 55%)
  `,
};
const CARD_CLASS =
    "rounded-2xl border border-white/10 bg-white/[.05] backdrop-blur-md shadow-[0_10px_30px_rgba(0,0,0,.25)]";

/* ---------- phases / resources ---------- */
const PHASES = ["Dark", "Feudal", "Castle", "Imperial"];
const RES = ["F", "W", "G", "S"];
const RES_PLUS_BUILD = ["F", "W", "G", "S", "build"];

/* ---------- chips (labels) - Aurora palette ---------- */
const LABEL_COLORS = {
    move: "bg-cyan-400/10 text-cyan-200 border-cyan-400/30",
    build: "bg-sky-400/10 text-sky-200 border-sky-400/30",
    queue: "bg-emerald-400/10 text-emerald-200 border-emerald-400/30",
    tech: "bg-violet-400/10 text-violet-200 border-violet-400/30",
    rally: "bg-teal-400/10 text-teal-200 border-teal-400/30",
    landmark: "bg-amber-400/10 text-amber-200 border-amber-400/30",
    note: "bg-slate-400/10 text-slate-200 border-slate-400/30",
};

const TOKEN_STYLES = {
    F: "bg-rose-400/10 text-rose-200 border-rose-400/30",
    FOOD: "bg-rose-400/10 text-rose-200 border-rose-400/30",
    W: "bg-emerald-400/10 text-emerald-200 border-emerald-400/30",
    WOOD: "bg-emerald-400/10 text-emerald-200 border-emerald-400/30",
    G: "bg-amber-400/10 text-amber-200 border-amber-400/30",
    GOLD: "bg-amber-400/10 text-amber-200 border-amber-400/30",
    S: "bg-slate-400/15 text-slate-200 border-slate-400/30",
    STONE: "bg-slate-400/15 text-slate-200 border-slate-400/30",
    BUILD: "bg-sky-400/10 text-sky-200 border-sky-400/30",
    __OBJECT: "bg-slate-300/10 text-slate-100 border-slate-400/25",
};

/* ---------- form primitives (Aurora glass) ---------- */
const Input = (props) => (
    <input
        {...props}
        className={
            "appearance-none px-3 py-2 text-sm font-medium border w-full rounded-xl " +
            "bg-white/5 text-slate-100 border-white/10 " +
            "placeholder:text-slate-400 " +
            "focus:outline-none focus:ring-2 focus:ring-cyan-400/30 focus:border-cyan-300/50 " +
            (props.className || "")
        }
    />
);

const Textarea = (props) => (
    <textarea
        {...props}
        className={
            "appearance-none px-3 py-2 text-sm w-full resize-none border rounded-xl " +
            "bg-white/5 text-slate-100 border-white/10 placeholder:text-slate-400 " +
            "focus:outline-none focus:ring-2 focus:ring-violet-400/30 focus:border-violet-300/50 " +
            (props.className || "")
        }
    />
);

const SelectField = ({ value, onChange, options, className }) => (
    <div className={"relative " + (className || "")}>
        <select
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className={
                "w-full appearance-none px-3 py-2 pr-8 text-sm font-medium w-full border rounded-xl " +
                "bg-white/5 text-slate-100 border-white/10 " +
                "focus:outline-none focus:ring-2 focus:ring-cyan-400/30 focus:border-cyan-300/50"
            }
        >
            {options.map((o) =>
                typeof o === "string" ? (
                    <option key={o} value={o} className="bg-slate-900">
                        {o}
                    </option>
                ) : (
                    <option key={o.value} value={o.value} className="bg-slate-900">
                        {o.label}
                    </option>
                )
            )}
        </select>
        <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
    </div>
);

const Button = ({ children, onClick, className = "", disabled, title }) => (
    <button
        onClick={onClick}
        disabled={disabled}
        title={title}
        className={
            "text-sm px-3 py-2 rounded-xl border font-semibold transition-colors " +
            "bg-white/5 border-white/10 text-slate-100 hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed " +
            className
        }
    >
        {children}
    </button>
);

const IconButton = ({ children, onClick, className = "", disabled, title }) => (
    <button
        onClick={(e) => {
            e.stopPropagation();
            onClick?.(e);
        }}
        disabled={disabled}
        title={title}
        className={
            "p-1.5 rounded-lg border bg-white/5 border-white/10 hover:bg-white/10 disabled:opacity-50 " +
            "text-slate-200 " +
            className
        }
    >
        {children}
    </button>
);

const Card = ({ title, children }) => (
    <section className={`${CARD_CLASS} p-4`}>
        {title && <div className="font-bold text-sm mb-2 text-slate-100">{title}</div>}
        <div className="text-slate-200">{children}</div>
    </section>
);

/* ---------- badges / labels (chips) ---------- */
const ResourceBadge = ({ r }) => {
    const raw = String(r ?? "").trim();
    if (!raw) return null;
    const upper = raw.toUpperCase().replace(/\s+/g, "");
    const m = upper.match(/^(\d+)?([A-Z_]+)$/);
    const qty = m?.[1] ?? "";
    const key = m?.[2] ?? upper;
    const known = TOKEN_STYLES[key] ?? TOKEN_STYLES[key.replace(/[0-9]/g, "")];
    const isCapsWord = /^[A-Z0-9_]+$/.test(key);
    const style = known ?? (isCapsWord ? TOKEN_STYLES.__OBJECT : "bg-slate-300/10 text-slate-100 border-slate-400/25");
    return (
        <span className={`inline-flex items-center px-1.5 py-0.5 text-[11px] font-semibold border rounded-md ${style}`}>
      {qty}{key}
    </span>
    );
};

function renderWithBadges(text) {
    if (!text) return null;
    const parts = String(text).split(/(\[[^\]]+])/g).filter(Boolean);
    return parts.map((part, i) => {
        const m = part.match(/^\[([^\]]+)]$/);
        if (m) return <ResourceBadge key={i} r={m[1]} />;
        return <span key={i} className="whitespace-nowrap">{part}</span>;
    });
}

const LabelChip = ({ type = "note", text = "", ghost = false }) => {
    const colorClass = LABEL_COLORS[type] || LABEL_COLORS.note;
    return (
        <div
            className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl border text-[12px] font-medium ${colorClass} ${
                ghost ? "opacity-70 ring-2 ring-cyan-300/20" : ""
            }`}
        >
            {renderWithBadges(text)}
        </div>
    );
};

/* ---------- chip label synthesis ---------- */
function chipToLabel(chip) {
    switch (chip.type) {
        case "new":
            return { type: "move", text: `New [${chip.count || 1}] → [${(chip.to || "F").toUpperCase()}]` };
        case "move":
        case "shift":
            if (chip.count && !chip.from) {
                return { type: "move", text: `Move [${chip.count}${(chip.from || "F").toUpperCase()}] → [${(chip.to || "W").toUpperCase()}]` };
            }
            return { type: "move", text: `Move [${chip.count || 1}${(chip.from || "F").toUpperCase()}] → [${(chip.to || "W").toUpperCase()}]` };
        case "build":
            return {
                type: "build",
                text: `Move [${chip.with || 1}${(chip.from || "F").toUpperCase()}] → [BUILD] [${chip.what || "Building"}]${chip.returnTo ? ` → [${chip.returnTo.toUpperCase()}]` : ""}`,
            };
        case "queue":
            return { type: "queue", text: `Queue ${chip.count || 1}× ${chip.unit || "Unit"}${chip.cond ? ` (${chip.cond})` : ""}` };
        case "upgrade":
            return { type: "tech", text: `Research → [${chip.building || chip.icon || "Building"}] → ${chip.label || chip.key || ""}` };
        case "rally":
            return { type: "rally", text: `Rally → [${(chip.to || "F").toUpperCase()}]` };
        case "landmark":
            return { type: "landmark", text: chip.text || "Age up" };
        case "note":
            return { type: "note", text: chip.text || "Note" };
        default:
            return { type: "note", text: "" };
    }
}

/* ---------- worker widgets ---------- */
const EditablePill = ({ label, value, onChange, cls = "" }) => (
    <div className={`text-[11px] px-2 py-1 font-extrabold border rounded-lg flex items-center gap-1 ${cls}`}>
        <span className="font-normal opacity-90 mr-1">{label}</span>
        <input
            type="number"
            min={0}
            value={value}
            onChange={(e) => onChange(clamp(Number(e.target.value) || 0, 0, 999))}
            className="w-9 text-center bg-transparent border-0 outline-none focus:ring-0 appearance-none
                 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
        />
    </div>
);
const WorkerPillsEdit = ({ F, W, G, S, onChange }) => (
    <div className="grid grid-cols-2 gap-1.5 w-40">
        <EditablePill label="F" value={F} onChange={(v) => onChange("F", v)} cls="border-rose-400/30 bg-rose-400/10 text-rose-200" />
        <EditablePill label="W" value={W} onChange={(v) => onChange("W", v)} cls="border-emerald-400/30 bg-emerald-400/10 text-emerald-200" />
        <EditablePill label="G" value={G} onChange={(v) => onChange("G", v)} cls="border-amber-400/30 bg-amber-400/10 text-amber-200" />
        <EditablePill label="S" value={S} onChange={(v) => onChange("S", v)} cls="border-slate-400/30 bg-slate-400/15 text-slate-200" />
    </div>
);

/* ---------- inline chip editor ---------- */
const ChipInline = ({ type, children }) => {
    const tone = LABEL_COLORS[type] || LABEL_COLORS.note;
    return <div className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 border rounded-xl text-[12px] ${tone}`}>{children}</div>;
};

function ChipEditorInline({ chip, onSave, onCancel }) {
    const [tmp, setTmp] = useState(chip);
    useEffect(() => { setTmp(chip); }, [chip?.id]);
    const commit = () => { onSave(tmp); onCancel(); };
    if (!tmp) return null;

    if (tmp.type === "build") {
        return (
            <ChipInline type="build">
                <span>Move</span>
                <Input style={{ width: 60 }} type="number" min={1} value={tmp.with || 1} onChange={(e) => setTmp({ ...tmp, with: clamp(+e.target.value || 1, 1, 200) })} />
                <SelectField value={tmp.from || "F"} onChange={(v) => setTmp({ ...tmp, from: v })} options={RES} className="w-20" />
                <span>→</span> build <span>→</span>
                <Input className="w-44" placeholder="building" value={tmp.what || ""} onChange={(e) => setTmp({ ...tmp, what: e.target.value })} />
                <span>→</span>
                <SelectField value={tmp.returnTo || ""} onChange={(v) => setTmp({ ...tmp, returnTo: v })} options={["", ...RES].map(r => ({ value: r, label: r || "—" }))} className="w-20" />
                <button className="ml-1" title="Save" onClick={commit}><Check className="w-3.5 h-3.5" /></button>
            </ChipInline>
        );
    }
    if (tmp.type === "new" || tmp.type === "move") {
        const isNew = tmp.type === "new";
        return (
            <ChipInline type="move">
                {!isNew && (
                    <SelectField value={tmp.type} onChange={(v) => setTmp({ ...tmp, type: v })} options={[{ value: "move", label: "shift" }]} className="w-24" />
                )}
                <Input style={{ width: 60 }} type="number" min={1} value={tmp.count || 1} onChange={(e) => setTmp({ ...tmp, count: clamp(+e.target.value || 1, 1, 200) })} />
                {isNew ? (
                    <>
                        <span>→</span>
                        <SelectField value={tmp.to || "F"} onChange={(v) => setTmp({ ...tmp, to: v })} options={RES} className="w-20" />
                    </>
                ) : (
                    <>
                        <SelectField value={tmp.from || "F"} onChange={(v) => setTmp({ ...tmp, from: v })} options={RES_PLUS_BUILD} className="w-20" />
                        <span>→</span>
                        <SelectField value={tmp.to || "W"} onChange={(v) => setTmp({ ...tmp, to: v })} options={RES} className="w-20" />
                    </>
                )}
                <button className="ml-1" title="Save" onClick={commit}><Check className="w-3.5 h-3.5" /></button>
            </ChipInline>
        );
    }
    if (tmp.type === "queue") {
        return (
            <ChipInline type="queue">
                <span>Queue</span>
                <Input style={{ width: 60 }} type="number" min={1} value={tmp.count || 1} onChange={(e) => setTmp({ ...tmp, count: clamp(+e.target.value || 1, 1, 200) })} />
                <Input className="w-32" value={tmp.unit || ""} onChange={(e) => setTmp({ ...tmp, unit: e.target.value })} />
                <Input className="w-40" placeholder="cond" value={tmp.cond || ""} onChange={(e) => setTmp({ ...tmp, cond: e.target.value })} />
                <button className="ml-1" title="Save" onClick={commit}><Check className="w-3.5 h-3.5" /></button>
            </ChipInline>
        );
    }
    if (tmp.type === "upgrade") {
        return (
            <ChipInline type="tech">
                <Input className="w-28" placeholder="building" value={tmp.building || ""} onChange={(e) => setTmp({ ...tmp, building: e.target.value })} />
                <Input className="w-28" placeholder="key" value={tmp.key || ""} onChange={(e) => setTmp({ ...tmp, key: e.target.value })} />
                <Input className="w-44" placeholder="label" value={tmp.label || ""} onChange={(e) => setTmp({ ...tmp, label: e.target.value })} />
                <button className="ml-1" title="Save" onClick={commit}><Check className="w-3.5 h-3.5" /></button>
            </ChipInline>
        );
    }
    if (tmp.type === "rally") {
        return (
            <ChipInline type="rally">
                <span>Rally →</span>
                <SelectField value={tmp.to || "F"} onChange={(v) => setTmp({ ...tmp, to: v })} options={RES} className="w-20" />
                <button className="ml-1" title="Save" onClick={commit}><Check className="w-3.5 h-3.5" /></button>
            </ChipInline>
        );
    }
    if (tmp.type === "note" || tmp.type === "landmark") {
        return (
            <ChipInline type={tmp.type}>
                <SelectField value={tmp.type} onChange={(v) => setTmp({ ...tmp, type: v })} options={["note", "landmark"]} className="w-28" />
                <Input className="w-72" placeholder="Type text…" value={tmp.text || ""} onChange={(e) => setTmp({ ...tmp, text: e.target.value })} />
                <button className="ml-1" title="Save" onClick={commit}><Check className="w-3.5 h-3.5" /></button>
            </ChipInline>
        );
    }
    return null;
}

/* ---------- default chips for the picker ---------- */
const defaultChipFor = (type) => {
    switch (type) {
        case "new": return { type: "new", count: 1, to: "F" };
        case "move": return { type: "move", count: 1, from: "F", to: "W" };
        case "build": return { type: "build", with: 1, from: "F", what: "House", returnTo: "F" };
        case "queue": return { type: "queue", unit: "Spearman", count: 3 };
        case "upgrade": return { type: "upgrade", building: "Mill", key: "economy", label: "Wheelbarrow" };
        case "rally": return { type: "rally", to: "F" };
        case "landmark": return { type: "landmark", text: "Age II: Landmark" };
        case "note": return { type: "note", text: "Scout enemy base" };
        default: return { type: "note", text: "" };
    }
};

/* ---------- simplified demo data ---------- */
const DEFAULT_DATA = {
    id: "demo",
    name: "Demo Civ",
    description: "Click “Add action” on a step and pick a chip; then click chips to edit. Use arrows to reorder within the step.",
    armyCompositions: ["6–10 Spearmen, 10–16 Archers"],
    keyMechanics: ["Example mechanic"],
    buildOrder: [
        { id: "s", at: 0, time: "00:00", phase: "Dark", title: "Open", workers: { F: 6, W: 0, G: 0, S: 0 }, chips: [{ id: newUid(), type: "new", count: 6, to: "F" }] },
        { id: "t1", at: 60, time: "01:00", phase: "Dark", title: "Step 2", workers: { F: 8, W: 0, G: 2, S: 0 }, chips: [] },
        { id: "t2", at: 90, time: "01:30", phase: "Dark", title: "", workers: { F: 6, W: 0, G: 0, S: 0 }, chips: [] }
    ]
};

/* ---------- Chip view with reorder & delete ---------- */
function ChipPill({ chip, onEdit, onRemove, onMoveLeft, onMoveRight }) {
    const { type, text } = chipToLabel(chip);
    return (
        <div
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); onEdit?.(); }}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 border text-[12px] font-medium rounded-xl cursor-pointer ${(LABEL_COLORS[type] || LABEL_COLORS.note)}`}
            title="Click to edit"
        >
            {renderWithBadges(text)}
            <span className="flex items-center gap-1 ml-1">
        <IconButton title="Move left" onClick={onMoveLeft}><ArrowLeft className="w-3.5 h-3.5" /></IconButton>
        <IconButton title="Move right" onClick={onMoveRight}><ArrowRight className="w-3.5 h-3.5" /></IconButton>
        <IconButton title="Remove" onClick={onRemove}><Trash2 className="w-3.5 h-3.5" /></IconButton>
      </span>
        </div>
    );
}

/* ---------- Per-row Add Action Picker ---------- */
function AddActionPicker({ onPick }) {
    const groups = [
        {
            title: "Economy",
            items: [
                { t: "new", sample: chipToLabel(defaultChipFor("new")) },
                { t: "move", sample: chipToLabel(defaultChipFor("move")) },
                { t: "rally", sample: chipToLabel(defaultChipFor("rally")) },
            ],
        },
        {
            title: "Buildings",
            items: [{ t: "build", sample: chipToLabel(defaultChipFor("build")) }],
        },
        {
            title: "Military",
            items: [{ t: "queue", sample: chipToLabel(defaultChipFor("queue")) }],
        },
        {
            title: "Technology",
            items: [{ t: "upgrade", sample: chipToLabel(defaultChipFor("upgrade")) }],
        },
        {
            title: "Strategy",
            items: [
                { t: "landmark", sample: chipToLabel(defaultChipFor("landmark")) },
                { t: "note", sample: chipToLabel(defaultChipFor("note")) },
            ],
        },
    ];

    return (
        <div className={`${CARD_CLASS} p-3 mt-2`}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {groups.map((g) => (
                    <div key={g.title} className="">
                        <div className="text-xs font-semibold text-slate-300 mb-2">{g.title}</div>
                        <div className="flex flex-wrap gap-2">
                            {g.items.map((it) => (
                                <button
                                    key={it.t}
                                    onClick={() => onPick?.(it.t)}
                                    className="transition-transform hover:scale-[1.02]"
                                    title={`Add ${it.t}`}
                                >
                                    <LabelChip type={it.sample.type} text={it.sample.text} />
                                </button>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
            <div className="text-xs text-slate-400 mt-3">Tip: After adding, click the chip to edit values. Use arrows to reorder within the row.</div>
        </div>
    );
}

/* ---------- main ---------- */
export default function BuildOrderConfigurator() {
    const [data, setData] = useState(DEFAULT_DATA);
    const [editing, setEditing] = useState(null); // { stepIndex, chipId } | null
    const [openAddMenuIndex, setOpenAddMenuIndex] = useState(null);
    const [showJsonModal, setShowJsonModal] = useState(false);
    const [showLoadModal, setShowLoadModal] = useState(false);
    const [showLoadConfirm, setShowLoadConfirm] = useState(false);
    const [showSaveConfirm, setShowSaveConfirm] = useState(false);
    const [savedCivs, setSavedCivs] = useState([]);
    const [selectedCivId, setSelectedCivId] = useState('');
    const [currentlyLoadedCivId, setCurrentlyLoadedCivId] = useState(null);
    const [loadingCivs, setLoadingCivs] = useState(false);
    const [loadError, setLoadError] = useState(null);
    const [copySuccess, setCopySuccess] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [saving, setSaving] = useState(false);
    const fileRef = useRef(null);

    // Load civs on component mount
    useEffect(() => {
        loadCivs();
    }, []);

    const steps = useMemo(() => {
        const out = (data.buildOrder || []).map(s => ({ ...s, chips: [...(s.chips || [])] }));
        let last = { F: 6, W: 0, G: 0, S: 0 };
        for (let i = 0; i < out.length; i++) {
            if (!out[i].workers) out[i].workers = { ...last };
            last = { ...out[i].workers };
        }
        return out;
    }, [data.buildOrder]);

    /* helpers */
    const patchStep = (i, patch) => setData(d => {
        const arr = d.buildOrder.map((x, idx) => idx === i ? { ...x, ...patch } : x);
        return { ...d, buildOrder: arr };
    });
    const replaceStepPartial = (i, partial) => setData(d => {
        const arr = [...d.buildOrder];
        arr[i] = { ...arr[i], ...partial };
        return { ...d, buildOrder: arr };
    });
    const addStep = () => {
        const at = (data.buildOrder?.[data.buildOrder.length - 1]?.at || 0) + 30;
        const step = { id: `step_${data.buildOrder.length + 1}`, at, time: fmt(at), phase: "Dark", title: "", workers: { F: 6, W: 0, G: 0, S: 0 }, chips: [] };
        setData(d => ({ ...d, buildOrder: [...(d.buildOrder || []), step] }));
    };
    const removeStep = (i) => setData(d => ({ ...d, buildOrder: d.buildOrder.filter((_, idx) => idx !== i) }));
    const sortByTime = () => setData(d => ({ ...d, buildOrder: [...(d.buildOrder || [])].sort((a, b) => a.at - b.at) }));

    // helpers (place next to addStep/removeStep/sortByTime)
    const addStepAfter = (i) => {
        const prev = data.buildOrder?.[i];
        const at = clamp((prev?.at ?? 0) + 30, 0, 100000);
        const step = {
            id: `step_${newUid()}`,
            at,
            time: fmt(at),
            phase: prev?.phase ?? "Dark",
            title: "",
            workers: { ...(prev?.workers ?? { F: 6, W: 0, G: 0, S: 0 }) },
            chips: [],
        };
        setData(d => {
            const arr = [...(d.buildOrder || [])];
            arr.splice(i + 1, 0, step);
            return { ...d, buildOrder: arr };
        });
    };

    // Chips
    const addChipToStep = (stepIndex, type) => {
        const chip = { id: newUid(), ...defaultChipFor(type) };
        setData(d => {
            const arr = [...d.buildOrder];
            const s = { ...arr[stepIndex], chips: [...(arr[stepIndex].chips || []), chip] };
            arr[stepIndex] = s;
            return { ...d, buildOrder: arr };
        });
        setOpenAddMenuIndex(null);
    };

    const removeChip = (stepIndex, chipId) => {
        setData(d => {
            const stepsArr = [...d.buildOrder];
            const s = { ...stepsArr[stepIndex], chips: [...(stepsArr[stepIndex].chips || [])] };
            s.chips = s.chips.filter(c => c.id !== chipId);
            stepsArr[stepIndex] = s;
            return { ...d, buildOrder: stepsArr };
        });
    };

    const moveChip = (stepIndex, chipIndex, delta) => {
        setData(d => {
            const arr = [...d.buildOrder];
            const s = { ...arr[stepIndex], chips: [...(arr[stepIndex].chips || [])] };
            const i = clamp(chipIndex + delta, 0, s.chips.length - 1);
            if (i === chipIndex) return d;
            const tmp = s.chips[chipIndex];
            s.chips.splice(chipIndex, 1);
            s.chips.splice(i, 0, tmp);
            arr[stepIndex] = s;
            return { ...d, buildOrder: arr };
        });
    };

    const onWorkersChange = (stepIndex, k, v) => {
        const workers = { ...(data.buildOrder[stepIndex].workers) };
        workers[k] = v;
        replaceStepPartial(stepIndex, { workers });
    };

    const saveChip = (stepIndex, chipId, next) => setData(d => {
        const stepsArr = [...d.buildOrder];
        const s = { ...stepsArr[stepIndex], chips: [...(stepsArr[stepIndex].chips || [])] };
        s.chips = s.chips.map(c => c.id === chipId ? { ...c, ...next } : c);
        stepsArr[stepIndex] = s;
        return { ...d, buildOrder: stepsArr };
    });

    /* import/export helpers used in modal */
    const onFile = async (e) => {
        const f = e.target.files?.[0]; if (!f) return;
        try { const txt = await f.text(); const obj = JSON.parse(txt); setData(obj); }
        catch { alert("Invalid JSON"); }
        finally { e.target.value = ""; }
    };

    // JSON modal helpers
    const copyJsonFromModal = async () => {
        try {
            await navigator.clipboard.writeText(JSON.stringify(data, null, 2));
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        } catch (err) {
            console.error('Failed to copy: ', err);
        }
    };
    const downloadFromModal = () => {
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${data.name || data.id || "build"}.json`;
        a.click();
        URL.revokeObjectURL(url);
    };

    // DB ops
    const loadCivs = async () => {
        setLoadingCivs(true);
        setLoadError(null);
        try {
            const civs = await fetchCivs();
            setSavedCivs(civs);
        } catch (err) {
            console.error('Error loading civs:', err);
            setLoadError('Failed to load civilizations. Please try again later.');
        } finally {
            setLoadingCivs(false);
        }
    };

    const saveToDB = async () => {
        setSaving(true);
        setSaveError(null);
        setSaveSuccess(false);
        try {
            const civData = { name: data.name || 'Unnamed Build Order', data };
            const result = await addCiv(civData);
            if (result?.id) {
                setData(prev => ({ ...prev, id: result.id }));
                setSaveSuccess(true);
                await loadCivs();
                setTimeout(() => setSaveSuccess(false), 3000);
            } else {
                setSaveError('Save failed: No ID returned from server');
            }
        } catch (err) {
            console.error('Error saving to DB:', err);
            setSaveError(`Save failed: ${err.message || 'Unknown error'}`);
        } finally {
            setSaving(false);
        }
    };

    const handleLoadCiv = () => {
        if (!selectedCivId) {
            alert('Please select a civilization to load.');
            return;
        }
        setShowLoadConfirm(true);
    };

    const confirmLoadCiv = () => {
        const civ = savedCivs.find(c => c.id === selectedCivId);
        if (civ && civ.data) {
            setData(civ.data);
            setCurrentlyLoadedCivId(civ.id);
            setShowLoadConfirm(false);
        }
    };

    const handleSaveCiv = () => setShowSaveConfirm(true);

    const confirmSaveCiv = async () => {
        setShowSaveConfirm(false);
        setSaving(true);
        setSaveError(null);
        setSaveSuccess(false);

        try {
            const civData = { name: data.name || 'Unnamed Build Order', data };
            let result;
            if (currentlyLoadedCivId) {
                result = await updateCiv(currentlyLoadedCivId, civData);
            } else {
                result = await addCiv(civData);
                if (result?.id) setCurrentlyLoadedCivId(result.id);
            }
            if (result?.id) {
                setData(prev => ({ ...prev, id: result.id }));
                setSaveSuccess(true);
                await loadCivs();
                setTimeout(() => setSaveSuccess(false), 3000);
            } else {
                setSaveError('Save failed: No ID returned from server');
            }
        } catch (err) {
            console.error('Error saving to DB:', err);
            setSaveError(`Save failed: ${err.message || 'Unknown error'}`);
        } finally {
            setSaving(false);
        }
    };

    /* ----- render ----- */
    return (
        <div className="min-h-screen text-slate-200" style={AURORA_BG}>
            {/* Header */}
            <header className="sticky top-0 z-30">
                <div
                    className="border-b border-white/10 backdrop-blur-md"
                    style={{ background: "linear-gradient(to right, rgba(15,23,42,.65), rgba(15,23,42,.45))" }}
                >
                    <div className="max-w-7xl mx-auto px-3 sm:px-4 py-2.5">
                        {/* Desktop Header */}
                        <div className="hidden sm:flex items-center gap-3">
                            <div className="text-lg font-extrabold tracking-tight text-slate-100">
                                <FileJson className="inline w-5 h-5 mr-2" />
                                Build Order — Configurator
                            </div>
                            <div className="flex-1" />
                            <div className="flex items-center gap-2 text-sm">
                                {/* Civs Dropdown + Load/Save */}
                                <div className="flex items-center gap-2">
                                    <SelectField
                                        value={selectedCivId}
                                        onChange={setSelectedCivId}
                                        options={[
                                            { value: '', label: 'Select a civilization...' },
                                            ...savedCivs.map(civ => ({ value: civ.id, label: civ.name || `Civ ${civ.id}` }))
                                        ]}
                                        className="w-56"
                                    />
                                    <Button onClick={handleLoadCiv} disabled={!selectedCivId || loadingCivs}>
                                        <FolderOpen className="inline w-4 h-4 mr-1" />
                                        Load Civ
                                    </Button>
                                    <Button onClick={handleSaveCiv} disabled={saving}>
                                        <Save className="inline w-4 h-4 mr-1" />
                                        {saving ? 'Saving…' : 'Save Civ'}
                                    </Button>
                                </div>
                                <Button onClick={addStep}><Plus className="inline w-4 h-4 mr-1" />New step</Button>
                                <Button onClick={() => setShowJsonModal(true)}><Eye className="inline w-4 h-4 mr-1" />View JSON</Button>
                            </div>
                        </div>

                        {/* Mobile Header */}
                        <div className="flex sm:hidden items-center justify-between">
                            <div className="text-lg font-extrabold tracking-tight text-slate-100">
                                Build Order
                            </div>
                            <div className="flex items-center gap-2">
                                <Button onClick={() => setShowJsonModal(true)} className="text-xs px-2 py-1">
                                    <Eye className="inline w-4 h-4" />
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            {/* Success/Error banners */}
            {(saveSuccess || saveError) && (
                <div className="max-w-7xl mx-auto px-4 pt-4">
                    {saveSuccess && (
                        <div className={`${CARD_CLASS} border-emerald-400/30 ring-1 ring-emerald-400/10 text-emerald-200 px-4 py-3 mb-4 flex items-center gap-2`}>
                            <Check className="w-4 h-4" />
                            Build order successfully saved to database!
                        </div>
                    )}
                    {saveError && (
                        <div className={`${CARD_CLASS} border-rose-400/30 ring-1 ring-rose-400/10 text-rose-200 px-4 py-3 mb-4`}>
                            {saveError}
                        </div>
                    )}
                </div>
            )}

            <main className="max-w-7xl mx-auto px-4 py-6 pb-24">
                {/* Heading */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-slate-100 mb-2">Build Order Designer</h1>
                    <p className="text-slate-400">Click “Add action” per row to insert chips; click chips to edit; arrows to reorder.</p>

                    {currentlyLoadedCivId && (
                        <div className={`${CARD_CLASS} mt-4 p-3 flex items-center gap-3`}>
                            <FileJson className="w-4 h-4 text-slate-200" />
                            <span className="text-sm">
                Currently editing:{" "}
                                <strong className="text-slate-100">
                  {savedCivs.find(c => c.id === currentlyLoadedCivId)?.name || 'Unknown Civilization'}
                </strong>
              </span>
                            <span className="text-xs text-slate-200/90 bg-white/5 px-2 py-1 border border-white/10 rounded-md">
                Changes update this civ when saved
              </span>
                        </div>
                    )}
                </div>

                {/* Info Cards */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                    <Card>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-slate-300 mb-2">Build Order Name</label>
                                <Input
                                    value={data.name || ""}
                                    onChange={(e) => setData(d => ({ ...d, name: e.target.value }))}
                                    placeholder="e.g., English Fast Castle"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-300 mb-2">Description</label>
                                <Textarea
                                    value={data.description || ""}
                                    onChange={(e) => setData(d => ({ ...d, description: e.target.value }))}
                                    placeholder="Describe your build order strategy..."
                                    rows={3}
                                />
                            </div>
                        </div>
                    </Card>

                    <Card title="Army Compositions">
                        <div>
                            <label className="block text-sm font-medium text-slate-400 mb-2">Target Army (one per line)</label>
                            <Textarea
                                value={(data.armyCompositions || []).join('\n')}
                                onChange={(e) => setData(d => ({
                                    ...d,
                                    armyCompositions: e.target.value.split('\n').filter(line => line.trim())
                                }))}
                                placeholder={"6-8 Spearmen, 12-16 Archers\n8 Knights, 6 Crossbowmen\n20 Longbowmen, 4 Trebuchets"}
                                rows={4}
                            />
                        </div>
                    </Card>

                    <Card title="Key Strategy Points">
                        <div>
                            <label className="block text-sm font-medium text-slate-400 mb-2">Core Mechanics (one per line)</label>
                            <Textarea
                                value={(data.keyMechanics || []).join('\n')}
                                onChange={(e) => setData(d => ({
                                    ...d,
                                    keyMechanics: e.target.value.split('\n').filter(line => line.trim())
                                }))}
                                placeholder={"Fast Castle rush with early Knights\nConstant villager production\nEarly harassment with scouts"}
                                rows={4}
                            />
                        </div>
                    </Card>
                </div>

                {/* Build Order Table */}
                <section className={`${CARD_CLASS} p-0`}>
                    <div className="p-4 border-b border-white/10">
                        <div className="flex items-center justify-between">
                            <h2 className="text-lg font-bold">Build Order Steps</h2>
                            <div className="flex items-center gap-2">
                                <Button onClick={sortByTime} className="text-xs">Sort by time</Button>
                                <Button onClick={() => setShowJsonModal(true)} className="text-xs"><Eye className="w-4 h-4 mr-1 inline" />JSON</Button>
                            </div>
                        </div>
                    </div>

                    {/* Desktop Table View */}
                    <div className="hidden md:block overflow-x-auto">
                        <table className="w-full">
                            <thead>
                            <tr className="border-b border-white/10 bg-white/5">
                                <th className="text-left py-3 px-4 font-medium text-slate-300 text-xs uppercase tracking-wide">Time</th>
                                <th className="text-left py-3 px-4 font-medium text-slate-300 text-xs uppercase tracking-wide">Population</th>
                                <th className="text-left py-3 px-4 font-medium text-slate-300 text-xs uppercase tracking-wide w-2/3">Actions & Notes</th>
                                <th className="w-20 py-3 px-4"></th>
                            </tr>
                            </thead>
                            <tbody className="divide-y divide-white/10">
                            {steps.map((step, index) => {
                                return (
                                    <>
                                    <tr key={step.id} className="bg-transparent hover:bg-white/5 transition-colors">
                                        <td className="py-4 px-4 align-top">
                                            <div className="space-y-2">
                                                <Input
                                                    className="w-28 text-center font-mono"
                                                    value={step.time}
                                                    onChange={(e) => {
                                                        const timeStr = e.target.value;
                                                        const seconds = parseTime(timeStr);
                                                        patchStep(index, { time: timeStr, at: seconds });
                                                    }}
                                                />
                                                <div className="flex items-center gap-2">
                                                    <Input
                                                        type="number"
                                                        min={0}
                                                        className="w-24 text-center text-xs font-mono"
                                                        value={step.at}
                                                        onChange={(e) => patchStep(index, {
                                                            at: clamp(Number(e.target.value) || 0, 0, 100000),
                                                            time: fmt(clamp(Number(e.target.value) || 0, 0, 100000))
                                                        })}
                                                        placeholder="sec"
                                                    />
                                                    <SelectField
                                                        value={step.phase || "Dark"}
                                                        onChange={(v) => patchStep(index, { phase: v })}
                                                        options={PHASES}
                                                        className="w-28"
                                                    />
                                                </div>
                                            </div>
                                        </td>

                                        <td className="py-4 px-4 align-top">
                                            <WorkerPillsEdit {...(step.workers || { F: 0, W: 0, G: 0, S: 0 })} onChange={(k, v) => onWorkersChange(index, k, v)} />
                                        </td>

                                        <td className="py-4 px-4 align-top">
                                            <div className="space-y-3">
                                                <Input
                                                    className="w-full"
                                                    placeholder="Step title or notes..."
                                                    value={step.title || ""}
                                                    onChange={(e) => patchStep(index, { title: e.target.value })}
                                                />

                                                {/* Chips container */}
                                                <div className="flex flex-wrap gap-2 min-h-[58px] p-3 rounded-xl border-2 border-white/15 bg-white/5">
                                                    {(step.chips || []).length === 0 ? (
                                                        <div className="text-slate-400 text-sm italic">
                                                            No actions yet—use “Add action”.
                                                        </div>
                                                    ) : (
                                                        (step.chips || []).map((chip, i) => (
                                                            <ChipPill
                                                                key={chip.id}
                                                                chip={chip}
                                                                onEdit={() => setEditing({ stepIndex: index, chipId: chip.id })}
                                                                onRemove={() => removeChip(index, chip.id)}
                                                                onMoveLeft={() => moveChip(index, i, -1)}
                                                                onMoveRight={() => moveChip(index, i, +1)}
                                                            />
                                                        ))
                                                    )}
                                                </div>

                                                {/* Inline Editor */}
                                                {editing && editing.stepIndex === index && (() => {
                                                    const chip = (step.chips || []).find(c => c.id === editing.chipId);
                                                    if (!chip) return null;
                                                    return (
                                                        <div className={`${CARD_CLASS} w-full mt-2 p-2`}>
                                                            <ChipEditorInline
                                                                chip={chip}
                                                                onSave={(next) => saveChip(index, chip.id, next)}
                                                                onCancel={() => setEditing(null)}
                                                            />
                                                        </div>
                                                    );
                                                })()}

                                                {/* Add Action */}
                                                <div>
                                                    <Button
                                                        onClick={() => setOpenAddMenuIndex(openAddMenuIndex === index ? null : index)}
                                                        className="text-xs"
                                                    >
                                                        <Plus className="w-4 h-4 mr-1 inline" />
                                                        Add action
                                                        {openAddMenuIndex === index ? <ChevronUp className="w-4 h-4 ml-1 inline" /> : <ChevronDown className="w-4 h-4 ml-1 inline" />}
                                                    </Button>
                                                    {openAddMenuIndex === index && (
                                                        <AddActionPicker
                                                            onPick={(t) => addChipToStep(index, t)}
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                        </td>

                                        <td className="py-4 px-4 align-top">
                                            <Button
                                                onClick={() => removeStep(index)}
                                                className="text-rose-200 hover:bg-rose-400/10 border-rose-400/30"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </td>
                                    </tr>
                                <tr>
                                    <td colSpan={4} className="py-2 px-4">
                                        <Button onClick={() => addStepAfter(index)} className="w-full text-xs">
                                            <Plus className="w-4 h-4 mr-1 inline" />
                                            Add step below
                                        </Button>
                                    </td>
                                </tr>
                                </>
                                );
                            })}

                            </tbody>
                        </table>
                    </div>

                    {/* Mobile Card View */}
                    <div className="md:hidden">
                        {steps.map((step, index) => {
                            return (
                                <div key={step.id} className="border-b border-white/10 last:border-b-0">
                                    <div className="p-4 space-y-4">
                                        {/* Step Header */}
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <div className="flex items-center gap-2">
                                                    <Input
                                                        className="w-20 text-center font-mono text-xs"
                                                        value={step.time}
                                                        onChange={(e) => {
                                                            const timeStr = e.target.value;
                                                            const seconds = parseTime(timeStr);
                                                            patchStep(index, { time: timeStr, at: seconds });
                                                        }}
                                                    />
                                                    <SelectField
                                                        value={step.phase || "Dark"}
                                                        onChange={(v) => patchStep(index, { phase: v })}
                                                        options={PHASES}
                                                        className="w-40"
                                                    />
                                                </div>
                                            </div>
                                            <Button
                                                onClick={() => removeStep(index)}
                                                className="text-rose-200 hover:bg-rose-400/10 border-rose-400/30 p-2"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </div>

                                        {/* Step Title */}
                                        <div>
                                            <Input
                                                className="w-full"
                                                placeholder="Step title or notes..."
                                                value={step.title || ""}
                                                onChange={(e) => patchStep(index, { title: e.target.value })}
                                            />
                                        </div>

                                        {/* Workers */}
                                        <div>
                                            <div className="text-xs font-medium text-slate-300 mb-2">Population</div>
                                            <WorkerPillsEdit {...(step.workers || { F: 0, W: 0, G: 0, S: 0 })} onChange={(k, v) => onWorkersChange(index, k, v)} />
                                        </div>

                                        {/* Chips */}
                                        <div>
                                            <div className="text-xs font-medium text-slate-300 mb-2">Actions</div>
                                            <div className="w-full min-h-[80px] p-4 rounded-xl border-2 border-white/15 bg-white/5">
                                                <div className="flex flex-wrap gap-2">
                                                    {(step.chips || []).length === 0 ? (
                                                        <div className="text-slate-400 text-sm italic w-full text-center py-4">
                                                            No actions yet—use “Add action”.
                                                        </div>
                                                    ) : (
                                                        (step.chips || []).map((chip, i) => (
                                                            <ChipPill
                                                                key={chip.id}
                                                                chip={chip}
                                                                onEdit={() => setEditing({ stepIndex: index, chipId: chip.id })}
                                                                onRemove={() => removeChip(index, chip.id)}
                                                                onMoveLeft={() => moveChip(index, i, -1)}
                                                                onMoveRight={() => moveChip(index, i, +1)}
                                                            />
                                                        ))
                                                    )}

                                                    {editing && editing.stepIndex === index && (() => {
                                                        const chip = (step.chips || []).find(c => c.id === editing.chipId);
                                                        if (!chip) return null;
                                                        return (
                                                            <div className={`${CARD_CLASS} w-full mt-3 p-3`}>
                                                                <ChipEditorInline
                                                                    chip={chip}
                                                                    onSave={(next) => saveChip(index, chip.id, next)}
                                                                    onCancel={() => setEditing(null)}
                                                                />
                                                            </div>
                                                        );
                                                    })()}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Add action */}
                                        <div>
                                            <Button
                                                onClick={() => setOpenAddMenuIndex(openAddMenuIndex === index ? null : index)}
                                                className="text-xs"
                                            >
                                                <Plus className="w-4 h-4 mr-1 inline" />
                                                Add action
                                                {openAddMenuIndex === index ? <ChevronUp className="w-4 h-4 ml-1 inline" /> : <ChevronDown className="w-4 h-4 ml-1 inline" />}
                                            </Button>
                                            {openAddMenuIndex === index && (
                                                <AddActionPicker onPick={(t) => addChipToStep(index, t)} />
                                            )}
                                        </div>
                                    </div>
                                    <div className="pt-2">
                                        <Button onClick={() => addStepAfter(index)} className="w-full text-xs">
                                            <Plus className="w-4 h-4 mr-1 inline" />
                                            Add step below
                                        </Button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </section>
            </main>

            {/* ----- JSON Modal ----- */}
            {showJsonModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
                    <div className="w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
                        <div className={`${CARD_CLASS} !rounded-2xl w-full overflow-hidden`}>
                            <div className="flex items-center justify-between p-4 border-b border-white/10">
                                <h3 className="text-lg font-semibold text-slate-100">Build Order JSON</h3>
                                <button onClick={() => setShowJsonModal(false)} className="text-slate-400 hover:text-slate-200">
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            {(saveSuccess || saveError) && (
                                <div className="p-4 border-b border-white/10">
                                    {saveSuccess && (
                                        <div className={`${CARD_CLASS} border-emerald-400/30 ring-1 ring-emerald-400/10 text-emerald-200 px-3 py-2 mb-2 flex items-center gap-2`}>
                                            <Check className="w-4 h-4" />
                                            Saved! You can view it in the Civilizations page.
                                        </div>
                                    )}
                                    {saveError && (
                                        <div className={`${CARD_CLASS} border-rose-400/30 ring-1 ring-rose-400/10 text-rose-200 px-3 py-2 mb-2`}>
                                            {saveError}
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="p-4 overflow-auto flex-1 min-h-0 text-slate-100">
                <pre className="text-sm font-mono whitespace-pre-wrap break-words">
                  {JSON.stringify(data, null, 2)}
                </pre>
                            </div>

                            <div className="flex justify-between items-center gap-2 p-4 border-t border-white/10">
                                <Button
                                    onClick={saveToDB}
                                    className={`${saving ? 'opacity-50 cursor-not-allowed' : ''} px-4 py-2 flex items-center gap-2`}
                                    disabled={saving}
                                >
                                    <Save className="w-4 h-4" />
                                    {saving ? 'Saving…' : 'Save to DB'}
                                </Button>
                                <div className="flex gap-2">
                                    <Button onClick={copyJsonFromModal}>{copySuccess ? "Copied!" : "Copy"}</Button>
                                    <Button onClick={downloadFromModal}>Download</Button>
                                    <Button onClick={() => fileRef.current?.click()}>
                                        <Upload className="w-4 h-4 mr-1" />
                                        Import
                                    </Button>
                                    <Button onClick={() => setShowJsonModal(false)}>Close</Button>
                                </div>
                            </div>
                        </div>

                        <input
                            ref={fileRef}
                            type="file"
                            accept="application/json"
                            className="hidden"
                            onChange={onFile}
                        />
                    </div>
                </div>
            )}

            {/* ----- Load Modal (optional list browser) ----- */}
            {showLoadModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
                    <div className="w-full max-w-md">
                        <div className={`${CARD_CLASS} overflow-hidden`}>
                            <div className="flex items-center justify-between p-4 border-b border-white/10">
                                <h3 className="text-lg font-semibold text-slate-100">Load Build Order</h3>
                                <button onClick={() => setShowLoadModal(false)} className="text-slate-400 hover:text-slate-200">
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            <div className="p-4 flex-1 min-h-0">
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between">
                                        <h4 className="text-sm font-medium text-slate-300">Saved Civilizations</h4>
                                        <Button onClick={loadCivs} className={`${loadingCivs ? 'opacity-50 cursor-not-allowed' : ''}`} disabled={loadingCivs}>
                                            {loadingCivs ? 'Loading…' : 'Refresh'}
                                        </Button>
                                    </div>

                                    {loadError && <div className="text-rose-200 text-sm">{loadError}</div>}

                                    <div className="space-y-2">
                                        {savedCivs.length === 0 ? (
                                            <div className="text-slate-400 text-sm italic">No saved civilizations found. Create a new one or import from JSON.</div>
                                        ) : (
                                            savedCivs.map(civ => (
                                                <div
                                                    key={civ.id}
                                                    className={`${CARD_CLASS} p-3 cursor-pointer transition-all ${selectedCivId === civ.id ? 'ring-2 ring-cyan-400/25' : ''}`}
                                                    onClick={() => setSelectedCivId(civ.id)}
                                                >
                                                    <div className="flex items-center justify-between gap-2">
                                                        <div>
                                                            <div className="text-sm font-medium text-slate-100">{civ.name}</div>
                                                            <div className="text-xs text-slate-400">{civ.description}</div>
                                                        </div>
                                                        <Button
                                                            onClick={async (e) => {
                                                                e.stopPropagation();
                                                                try {
                                                                    await removeCiv(civ.id);
                                                                    await loadCivs();
                                                                    if (currentlyLoadedCivId === civ.id) setCurrentlyLoadedCivId(null);
                                                                    if (selectedCivId === civ.id) setSelectedCivId('');
                                                                } catch (err) {
                                                                    console.error(err);
                                                                }
                                                            }}
                                                            className="text-rose-200 hover:bg-rose-400/10 border-rose-400/30"
                                                            title="Delete civilization"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </Button>
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="flex justify-end gap-2 p-4 border-t border-white/10">
                                <Button onClick={() => setShowLoadModal(false)}>Close</Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Load Confirmation */}
            {showLoadConfirm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
                    <div className="w-full max-w-md">
                        <div className={`${CARD_CLASS}`}>
                            <div className="flex items-center justify-between p-4 border-b border-white/10">
                                <h3 className="text-lg font-semibold flex items-center gap-2 text-slate-100">
                                    <AlertTriangle className="w-5 h-5 text-amber-300" />
                                    Confirm Load Civilization
                                </h3>
                                <button onClick={() => setShowLoadConfirm(false)} className="text-slate-400 hover:text-slate-200">
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                            <div className="p-4">
                                <p className="text-slate-400 mb-4">
                                    Are you sure you want to load this civilization? This will overwrite your current build order configuration.
                                </p>
                                {selectedCivId && (
                                    <div className={`${CARD_CLASS} p-3`}>
                                        <div className="font-medium text-slate-100">
                                            {savedCivs.find(c => c.id === selectedCivId)?.name || 'Selected Civilization'}
                                        </div>
                                        <div className="text-sm text-slate-400">
                                            {savedCivs.find(c => c.id === selectedCivId)?.data?.description || 'No description available'}
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="flex justify-end gap-2 p-4 border-t border-white/10">
                                <Button onClick={() => setShowLoadConfirm(false)}>Cancel</Button>
                                <Button onClick={confirmLoadCiv} className="text-cyan-200 hover:bg-cyan-400/10 border-cyan-400/30">
                                    Load Civilization
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Save Confirmation */}
            {showSaveConfirm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
                    <div className="w-full max-w-md">
                        <div className={`${CARD_CLASS}`}>
                            <div className="flex items-center justify-between p-4 border-b border-white/10">
                                <h3 className="text-lg font-semibold flex items-center gap-2 text-slate-100">
                                    <Save className="w-5 h-5" />
                                    Confirm Save Civilization
                                </h3>
                                <button onClick={() => setShowSaveConfirm(false)} className="text-slate-400 hover:text-slate-200">
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                            <div className="p-4">
                                <p className="text-slate-400 mb-4">
                                    Are you sure you want to save this build order to the database?
                                </p>
                                <div className={`${CARD_CLASS} p-3`}>
                                    <div className="font-medium text-slate-100">
                                        {data.name || 'Unnamed Build Order'}
                                    </div>
                                    <div className="text-sm text-slate-400">
                                        {data.description || 'No description provided'}
                                    </div>
                                    <div className="text-xs text-slate-400 mt-1">
                                        {data.buildOrder?.length || 0} build order steps
                                    </div>
                                </div>
                            </div>
                            <div className="flex justify-end gap-2 p-4 border-t border-white/10">
                                <Button onClick={() => setShowSaveConfirm(false)}>Cancel</Button>
                                <Button onClick={confirmSaveCiv} className="text-emerald-200 hover:bg-emerald-400/10 border-emerald-400/30">
                                    Save to Database
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
